import { useEffect, useRef, useState } from 'react';

function useParallax(speed = 0.25) {
  const ref = useRef<HTMLDivElement>(null);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const el = ref.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const windowH = window.innerHeight;
      // How far the section centre is from viewport centre
      const centerDiff = rect.top + rect.height / 2 - windowH / 2;
      setOffset(centerDiff * speed);
    };

    // Run once on mount to set initial value
    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [speed]);

  return { ref, offset };
}

export default function BrandPhilosophy() {
  const { ref: sectionRef, offset } = useParallax(0.22);

  return (
    <section className="py-24 md:py-32 bg-[#fafaf8]">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="flex flex-col lg:flex-row lg:gap-20 items-center">

          {/* Left Image — parallax */}
          <div className="lg:w-1/2 mb-12 lg:mb-0" ref={sectionRef}>
            <div className="relative">
              {/* Overflow hidden container — fixed dimensions */}
              <div className="w-full h-[480px] md:h-[560px] rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=modern%20commercial%20interior%20design%20studio%20workspace%20architects%20designers%20reviewing%20plans%20elegant%20professional%20environment%20warm%20lighting%20natural%20materials&width=900&height=1100&seq=philosophy001&orientation=portrait"
                  alt="心旅商研品牌理念"
                  className="w-full h-[130%] object-cover object-center"
                  style={{
                    transform: `translateY(${offset}px)`,
                    willChange: 'transform',
                    transition: 'transform 0.05s linear',
                  }}
                />
              </div>

              {/* Floating badge */}
              <div className="absolute -bottom-6 -right-6 bg-[#b8956a] text-white rounded-2xl p-6 w-44">
                <div
                  className="font-bold mb-1"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '2.5rem', lineHeight: 1 }}
                >
                  12+
                </div>
                <div className="text-sm text-white/80">年商業空間<br />設計經驗</div>
              </div>

              {/* Decorative corner accent */}
              <div
                className="absolute -top-4 -left-4 w-20 h-20 rounded-xl border-2 border-[#b8956a]/30 pointer-events-none"
                aria-hidden="true"
              ></div>
            </div>
          </div>

          {/* Right Content */}
          <div className="lg:w-1/2">
            <p className="text-[#b8956a] text-sm font-medium tracking-[0.2em] uppercase mb-4">Brand Philosophy</p>
            <h2
              className="font-bold text-[#1a1a1a] leading-[1.35] mb-6"
              style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)' }}
            >
              為什麼「心旅商研」<br />
              <span
                className="font-light italic text-[#b8956a]"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(1.75rem, 3.5vw, 2.5rem)' }}
              >
                只專注商業空間設計
              </span>
            </h2>

            <div className="space-y-5 text-[#5a5a5a]" style={{ fontSize: '1.1875rem' }}>
              <p className="leading-[1.75]">
                商業空間的成敗，不只關乎美觀，更關乎品牌傳遞的訊息、消費者的體驗感受、
                以及空間如何協助品牌建立長期競爭優勢。
              </p>
              <p className="leading-[1.75]">
                我們相信，一個真正好的商業空間設計，應該同時兼顧：
                品牌識別的清晰傳達、消費動線的流暢體驗、坪效的最大化，
                以及可落地執行的工程品質。
              </p>
              <p className="leading-[1.75]">
                這就是「心旅商研」成立的初衷——讓每一個商業空間，
                都成為品牌最有力、最持久的資產。
              </p>
            </div>

            <div className="grid grid-cols-2 gap-6 mt-10 pt-10 border-t border-[#e2ddd8]">
              {[
                { num: '150+', label: '完成設計案例' },
                { num: '30,000+', label: '規劃落地坪數' },
                { num: '98%', label: '業主滿意率' },
                { num: '6大城市', label: '服務據點' },
              ].map((s) => (
                <div key={s.label}>
                  <div
                    className="font-bold text-[#1a1a1a]"
                    style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '2.25rem', lineHeight: 1.1 }}
                  >
                    {s.num}
                  </div>
                  <div className="text-[#8a8a8a] text-sm mt-1">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
